import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LOGO_ASSETS } from './Logo';

/**
 * LogoFlash — full-screen celebratory brand-mark animation.
 *
 * Use for big moments: RSVP confirmed, tournament bracket reveal, level up,
 * achievement unlock, milestone reached. Provides a quick (1.6s) on-brand
 * "moment" without blocking interaction.
 *
 * Props:
 *   show:     boolean — controls visibility
 *   onDone:   () => void called after animation completes
 *   message:  optional caption text underneath (e.g. "You're in!" or "LEVEL UP")
 *   accentColor: hex color used for the rim glow (default cyan)
 *   tone:     'success' (cyan) | 'celebration' (purple) | 'level-up' (gold)
 *
 * Renders a backdrop blur, the icon-only logo with a rim glow + scale-in animation,
 * a circular ripple, and the optional caption below.
 */

const TONE_COLORS = {
  success: '#00F0FF',
  celebration: '#9D4CDD',
  'level-up': '#F59E0B',
};

export function LogoFlash({ show, onDone, message, tone = 'celebration', accentColor }) {
  const color = accentColor || TONE_COLORS[tone] || TONE_COLORS.celebration;

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => { if (onDone) onDone(); }, 1600);
    return () => clearTimeout(t);
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[200] pointer-events-none flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="logo-flash"
          aria-live="polite"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Ripple ring */}
          <motion.div
            initial={{ scale: 0.4, opacity: 0.7 }}
            animate={{ scale: 2.4, opacity: 0 }}
            transition={{ duration: 1.4, ease: 'easeOut' }}
            className="absolute w-40 h-40 rounded-full"
            style={{ border: `2px solid ${color}`, boxShadow: `0 0 60px ${color}` }}
          />
          <motion.div
            initial={{ scale: 0.4, opacity: 0.5 }}
            animate={{ scale: 3.4, opacity: 0 }}
            transition={{ duration: 1.4, ease: 'easeOut', delay: 0.15 }}
            className="absolute w-40 h-40 rounded-full"
            style={{ border: `1px solid ${color}` }}
          />

          {/* Logo mark */}
          <motion.div
            initial={{ scale: 0.4, opacity: 0, rotate: -8 }}
            animate={{ scale: [0.4, 1.1, 1], opacity: 1, rotate: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.6, ease: 'backOut' }}
            className="relative flex flex-col items-center"
          >
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full blur-2xl"
                style={{ background: color, opacity: 0.45 }}
              />
              <img
                src={LOGO_ASSETS.icon}
                alt="Cyboard"
                width={140}
                height={140}
                className="relative select-none"
                draggable={false}
              />
            </div>
            {message && (
              <motion.p
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="mt-4 text-white text-base sm:text-lg font-bold tracking-[0.18em] uppercase"
                style={{ fontFamily: 'Unbounded, sans-serif', textShadow: `0 0 20px ${color}` }}
                data-testid="logo-flash-message"
              >
                {message}
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * useLogoFlash — convenience hook to fire one-off flashes.
 *   const { flash, FlashRenderer } = useLogoFlash();
 *   <FlashRenderer />
 *   // ...later
 *   flash({ message: "You're in!", tone: 'success' });
 */
export function useLogoFlash() {
  const [state, setState] = React.useState({ show: false, message: null, tone: 'celebration' });
  const flash = React.useCallback(({ message, tone = 'celebration' } = {}) => {
    setState({ show: true, message, tone });
  }, []);
  const FlashRenderer = React.useCallback(
    () => (
      <LogoFlash
        show={state.show}
        message={state.message}
        tone={state.tone}
        onDone={() => setState((s) => ({ ...s, show: false }))}
      />
    ),
    [state]
  );
  return { flash, FlashRenderer };
}

export default LogoFlash;
